import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { type ProductCostBreakdownSearchFilters, useProductCostBreakdownsSearchStore } from "../stores"
import type { ProductCostBreakdownRow } from "../types"
import { transformProductWarehouseItemsToRows } from "../utils"

import { SortingOrder } from "@/graphql/codegen/graphql"
import type { ProductWarehouseListSortField } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

interface UseProductCostBreakdownsTableReturn {
  rows: ProductCostBreakdownRow[]
  isLoading: boolean
  expandedState: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  handleExpandedChange: OnChangeFn<ExpandedState>
  handleSortingChange: OnChangeFn<SortingState>
  handleScroll: () => void
  handleRefetch: () => void
}

export const useProductCostBreakdownsTable = (): UseProductCostBreakdownsTableReturn => {
  const params = useParams({ from: "/app/product/$productId/edit" })
  const productId = Number(params.productId)

  const searchParams = useSearch({ from: "/app/product/$productId/edit/_layout/costs" })
  const { field: sortField, order: sortOrder } = searchParams

  const navigate = useNavigate()

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [expandedState, setExpandedState] = useState<ExpandedState>({})

  const data = useProductCostBreakdownsSearchStore((state) => state.data)
  const fetching = useProductCostBreakdownsSearchStore((state) => state.fetching)
  const executeSearch = useProductCostBreakdownsSearchStore((state) => state.executeSearch)
  const loadMore = useProductCostBreakdownsSearchStore((state) => state.loadMore)
  const clearStore = useProductCostBreakdownsSearchStore((state) => state.clearStore)

  const rows = useMemo(() => transformProductWarehouseItemsToRows(data, productId), [data, productId])

  const searchFilters: ProductCostBreakdownSearchFilters = useMemo(
    () => ({
      productId,
      field: sortField,
      order: sortOrder,
    }),
    [productId, sortField, sortOrder]
  )

  useEffect(() => {
    executeSearch(queryClient, searchFilters)

    return () => {
      clearStore()
    }
  }, [searchFilters, executeSearch, clearStore])

  const sortingState = useMemo(() => {
    if (!sortField) {
      return []
    }

    const tableSortField = upperSnakeCaseToCamelCase(sortField)

    return [{ id: tableSortField, desc: sortOrder === SortingOrder.Desc }]
  }, [sortField, sortOrder])

  const handleExpandedChange: OnChangeFn<ExpandedState> = useCallback((updater) => {
    setExpandedState((old) => (typeof updater === "function" ? updater(old) : updater))
  }, [])

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setExpandedState({})

      const newSorting = typeof updater === "function" ? updater(sortingState) : updater

      if (newSorting.length > 0) {
        const sort = newSorting[0]
        const field = toUpperSnakeCase(sort.id) as ProductWarehouseListSortField
        const order = sort.desc ? SortingOrder.Desc : SortingOrder.Asc

        navigate({
          to: "/app/product/$productId/edit/costs",
          params,
          search: { field, order },
          replace: true,
        })
      }
    },
    [sortingState, navigate, params]
  )

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, searchFilters)
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleRefetch = useCallback(() => {
    clearStore()
    executeSearch(queryClient, searchFilters)
  }, [searchFilters, executeSearch, clearStore])

  return {
    rows,
    isLoading: fetching,
    expandedState,
    sortingState,
    tableContainerRef,
    handleExpandedChange,
    handleSortingChange,
    handleScroll,
    handleRefetch,
  }
}
